import React, { useEffect, useRef, useState } from "react";
import { message } from "antd";
import { RiFileList3Line } from "react-icons/ri";
import TemplateSendModal from "./Templates/TemplateSendModal";

function SendIcon() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
      <path d="M1.101 21.757L23.8 12.028 1.101 2.3l.011 7.912 13.623 1.816-13.623 1.817-.011 7.912z" />
    </svg>
  );
}

export default function Footer({
  currentPhone,
  messaging,
  contacts,
  emojiOpen,
  setEmojiOpen,
  onUserInteraction,
  replyTo = null,
  onCancelReply = null,
  isLive = true,
}) {
  const [text, setText] = useState("");
  const [media, setMedia] = useState(null);
  const [uploadLabel, setUploadLabel] = useState("");
  const [sending, setSending] = useState(false);
  const [removingMedia, setRemovingMedia] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const txRef = useRef(null);
  const fileInputRef = useRef(null);

  const adjustHeight = () => {
    const el = txRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 100)}px`;
  };

  useEffect(() => {
    adjustHeight();
  }, [text]);

  const clearMediaState = () => {
    setMedia(null);
    setUploadLabel("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const clearMedia = async () => {
    if (!media) {
      clearMediaState();
      return;
    }

    setRemovingMedia(true);
    try {
      if (media.s3_key && messaging?.deleteMediaUpload) {
        await messaging.deleteMediaUpload(media.s3_key);
      }
      clearMediaState();
      message.success("Attachment removed.");
    } catch (err) {
      message.error(
        err.response?.data?.message || err.message || "Could not remove attachment"
      );
    } finally {
      setRemovingMedia(false);
    }
  };

  const contactNameForSend = () => {
    const c = (contacts || []).find(
      (x) => String(x.phone) === String(currentPhone)
    );
    return c?.nameOnly ? String(c.nameOnly).trim() : "";
  };

  const sendReply = () => {
    if (!text && !media) return;
    if (!currentPhone) {
      message.warning("Select a contact first.");
      return;
    }
    if (!messaging) return;

    if (media) {
      setSending(true);
      messaging
        .sendMedia(currentPhone, media.type, "", {
          contact_name: contactNameForSend(),
          caption: text,
          cdn_url: media.cdn_url,
          s3_key: media.s3_key,
          mime_type: media.mime_type,
          file_size: media.file_size,
          file_name: media.filename,
          media,
        })
        .then(() => {
          setText("");
          clearMediaState();
          if (txRef.current) txRef.current.style.height = "40px";
        })
        .catch((err) => {
          message.error(
            err.response?.data?.message || err.message || "Media send failed"
          );
        })
        .finally(() => setSending(false));
      return;
    }

    let contextText = "Message";
    if (replyTo) {
      if (replyTo.text) contextText = replyTo.text;
      else if (replyTo.template?.name) contextText = `Template: ${replyTo.template.name}`;
      else if (replyTo.has_media || (replyTo.media && replyTo.media.length)) contextText = "Media";
    }

    const context = replyTo
      ? {
          message_id: replyTo.wa_message_id,
          text: contextText,
        }
      : null;

    setSending(true);
    messaging
      .sendReply(currentPhone, text, contactNameForSend(), context)
      .then(() => {
        setText("");
        setMedia(null);
        setUploadLabel("");
        onCancelReply?.();
        if (fileInputRef.current) fileInputRef.current.value = "";
        if (txRef.current) txRef.current.style.height = "40px";
      })
      .catch((err) => {
        message.error(
          err.response?.data?.message || err.message || "Send failed"
        );
      })
      .finally(() => setSending(false));
  };

  const mediaTypeForFile = (file) => {
    const mime = String(file?.type || "").toLowerCase();
    if (mime.startsWith("image/")) return "image";
    if (mime.startsWith("video/")) return "video";
    if (mime.startsWith("audio/")) return "audio";
    return "document";
  };

  const uploadFile = async (file) => {
    if (!file) return;
    if (!currentPhone) {
      message.warning("Select a contact first.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    if (!messaging) return;
    if (media) {
      message.warning("Remove the current attachment before choosing another file.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    const type = mediaTypeForFile(file);
    setUploadLabel("Uploading attachment...");
    try {
      const presign = await messaging.createMediaUpload(currentPhone, file, type);
      const uploadRes = await fetch(presign.upload_url, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!uploadRes.ok) {
        throw new Error(`S3 upload failed (${uploadRes.status})`);
      }
      setMedia({
        ...(presign.media || {}),
        type,
        cdn_url: presign.cdn_url,
        s3_key: presign.s3_key,
        mime_type: file.type,
        file_size: file.size,
        filename: file.name,
      });
      setUploadLabel("Attachment ready to send.");
    } catch (err) {
      setUploadLabel("");
      message.error(
        err.response?.data?.message || err.message || "Attachment upload failed"
      );
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const addEmoji = (emoji) => {
    setText((t) => t + emoji);
    setEmojiOpen(false);
    txRef.current?.focus();
  };

  return (
    <div className="footer">
      <div className="footer-btns">
        <span
          data-testid="emoji-picker-trigger"
          role="button"
          tabIndex={0}
          onClick={() => setEmojiOpen(!emojiOpen)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") setEmojiOpen(!emojiOpen);
          }}
        >
          🙂
        </span>
        <label htmlFor="fileInput">📎</label>
        <button
          data-testid="template-picker-trigger"
          type="button"
          onClick={() => isLive && setTemplateOpen(true)}
          title={isLive ? "Send template" : "Subscription required"}
          aria-label="Send template"
          disabled={!isLive}
          style={{ 
            background: "none", 
            border: "none", 
            padding: 0, 
            color: "inherit",
            opacity: isLive ? 1 : 0.5, 
            cursor: isLive ? "pointer" : "not-allowed",
            display: "inline-flex",
            alignItems: "center"
          }}
        >
          <RiFileList3Line size={22} />
        </button>
        <input
          ref={fileInputRef}
          type="file"
          id="fileInput"
          style={{ display: "none" }}
          onChange={(e) => {
            const f = e.target.files && e.target.files[0];
            uploadFile(f);
            e.target.value = "";
          }}
          onFocus={onUserInteraction}
          onClick={onUserInteraction}
        />
      </div>

      <div
        style={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
        }}
      >
        <span
          id="uploadStatus"
          className="upload-indicator"
          style={{
            display: uploadLabel || media ? "block" : "none",
            marginBottom: 5,
          }}
        >
          {media ? (
            <>
              Attachment: {media.filename || media.fileName || "Ready to send"}{" "}
              <span
                role="button"
                tabIndex={0}
                onClick={clearMedia}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") clearMedia();
                }}
                aria-label="Remove attachment"
                style={{
                  color: removingMedia ? "#999" : "red",
                  cursor: removingMedia ? "wait" : "pointer",
                  marginLeft: 8,
                  pointerEvents: removingMedia ? "none" : "auto",
                }}
              >
                {removingMedia ? "Removing..." : "Remove"}
              </span>
            </>
          ) : (
            uploadLabel
          )}
        </span>
        {replyTo && (
          <div className="footer-reply-preview">
            <div className="footer-reply-line" />
            <div className="footer-reply-content">
              <div className="footer-reply-name">
                {replyTo.direction === "outbound" ? "You" : (replyTo.contact_name || "User")}
              </div>
              <div className="footer-reply-text">
                {replyTo.text || (replyTo.has_media ? "Media" : "Message")}
              </div>
            </div>
            <button
              type="button"
              className="footer-reply-close"
              onClick={onCancelReply}
            >
              ×
            </button>
          </div>
        )}
        <textarea
          data-testid="message-textarea"
          ref={txRef}
          id="messageText"
          value={text}
          disabled={sending || !isLive}
          onChange={(e) => setText(e.target.value)}
          onInput={adjustHeight}
          placeholder={isLive ? "Type a message..." : "Subscription required to send messages"}
          rows={1}
          onFocus={onUserInteraction}
          onClick={onUserInteraction}
        />
      </div>

      <button
        data-testid="send-message-button"
        id="sendBtn"
        type="button"
        onClick={sendReply}
        disabled={sending || !isLive}
        style={{ opacity: (sending || !isLive) ? 0.5 : 1, cursor: isLive ? "pointer" : "not-allowed" }}
      >
        <SendIcon />
      </button>

      {emojiOpen && <EmojiGrid onSelect={addEmoji} />}
      <TemplateSendModal
        open={templateOpen}
        currentPhone={currentPhone}
        contactName={contactNameForSend()}
        contacts={contacts}
        messaging={messaging}
        onCancel={() => setTemplateOpen(false)}
        onSent={() => setTemplateOpen(false)}
      />
    </div>
  );
}

function EmojiGrid({ onSelect }) {
  const emojis = [
    "🙂", "😊", "😉", "😜", "😂", "🤣", "😍", "😎", "🤔", "😅", "🙄", "😱", "😴", "❤️", "✨",
    "👍🏼", "👎🏼", "👌🏼", "✌🏼", "🤝🏼", "🙌🏼", "🙏🏼", "👏🏼", "💪🏼", "👋🏼", "🤞🏼", "✍🏼", "✅", "❌", "⚠️",
    "📍", "⏳", "🔔", "📁", "📝", "📞", "📧", "💰", "💸", "⭐", "🚀", "💡", "🎉", "🎁", "🔥",
  ];
  return (
    <div className="emoji-picker emoji-picker-popup">
      {emojis.map((emo) => (
        <span key={emo} role="presentation" onClick={() => onSelect(emo)}>
          {emo}
        </span>
      ))}
    </div>
  );
}
